import React, { useState, useEffect, useRef } from 'react';
import { Card, Tabs, Input, InputNumber, Button, Spin, message, Space, Upload, Divider, Row, Col, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { TextArea } = Input;

const SUPPORTED_LANGS = ['fa', 'en', 'ja', 'de', 'pt', 'fr', 'it', 'es', 'ko', 'hi', 'zh'];
const LANG_LABELS = { fa: 'فارسی', en: 'English', ja: '日本語', de: 'Deutsch', pt: 'Português', fr: 'Français', it: 'Italiano', es: 'Español', ko: '한국어', hi: 'हिन्दी', zh: '中文' };

const EMPTY_ITEM = { icon: '✨', image: '', imageWidth: '', imageHeight: '', imageWidthMobile: '', imageHeightMobile: '', title: '', desc: '' };
const CONTACT_EMPTY_ITEM = { icon: '📞', image: '', imageWidth: '', imageHeight: '', imageWidthMobile: '', imageHeightMobile: '', label: '', url: '' };

// ── متن‌های قابل‌تنظیم‌سایز صفحه اصلی — هر نقش یک اندازه‌ی دسکتاپ و یک اندازه‌ی
// موبایل (px، اختیاری) دارد. این لیست باید با نقش‌هایی که در LandingPage.jsx
// با tsVars(...) خوانده می‌شوند و با متغیرهای CSS در LandingPage.module.css
// (--ts-<role>-d / --ts-<role>-m) هماهنگ بماند.
const TEXT_SIZE_ROLES = [
  { key: 'heroTitle', label: 'عنوان اصلی هیرو' },
  { key: 'heroSubtitle', label: 'توضیح زیر عنوان هیرو' },
  { key: 'storyTitle', label: 'عنوان بخش «مسیر ورود»' },
  { key: 'storyDesc', label: 'توضیح بخش «مسیر ورود»' },
  { key: 'journeyCardTitle', label: 'عنوان کارت‌های مراحل' },
  { key: 'journeyCardDesc', label: 'توضیح کارت‌های مراحل' },
  { key: 'featuresTitle', label: 'عنوان بخش «ویژگی‌ها»' },
  { key: 'featureCardTitle', label: 'عنوان کارت‌های ویژگی' },
  { key: 'featureCardDesc', label: 'توضیح کارت‌های ویژگی' },
  { key: 'magicShowcaseTitle', label: 'عنوان «ویترین جادویی»' },
  { key: 'magicShowcaseDesc', label: 'توضیح «ویترین جادویی»' },
  { key: 'showcaseCardTitle', label: 'عنوان کارت‌های ویترین جادویی' },
  { key: 'showcaseCardDesc', label: 'توضیح کارت‌های ویترین جادویی' },
  { key: 'moreCardTitle', label: 'عنوان کارت‌های «ویژگی‌های بیشتر»' },
  { key: 'moreCardDesc', label: 'توضیح کارت‌های «ویژگی‌های بیشتر»' },
  { key: 'waitlistTitle', label: 'عنوان لیست انتظار' },
  { key: 'waitlistDesc', label: 'توضیح لیست انتظار' },
  { key: 'contactTitle', label: 'عنوان تماس با ما' },
  { key: 'contactDesc', label: 'توضیح تماس با ما' },
];

// ── ویرایشگر اندازه‌ی متن‌ها — بین فارسی/انگلیسی مشترک است (یک تنظیم ظاهری،
// نه محتوا)، پس مستقل از تب زبان فعال، مستقیم روی content.textSizes کار می‌کند.
function TextSizeEditor({ sizes, onChange }) {
  const update = (key, field, v) => {
    const next = { ...sizes };
    next[key] = { ...(next[key] || {}), [field]: v || '' };
    onChange(next);
  };
  return (
    <div>
      {TEXT_SIZE_ROLES.map((role) => {
        const s = sizes?.[role.key] || {};
        return (
          <Row gutter={8} key={role.key} align="middle" style={{ marginBottom: 8 }}>
            <Col flex="220px">{role.label}</Col>
            <Col flex="130px">
              <InputNumber
                size="small"
                min={0}
                value={s.desktop || undefined}
                onChange={(v) => update(role.key, 'desktop', v)}
                placeholder="دسکتاپ (px)"
                style={{ width: '100%' }}
              />
            </Col>
            <Col flex="130px">
              <InputNumber
                size="small"
                min={0}
                value={s.mobile || undefined}
                onChange={(v) => update(role.key, 'mobile', v)}
                placeholder="موبایل (px)"
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        );
      })}
    </div>
  );
}

// پیش‌فرض بخش «تماس با ما» — این کلیدها بعداً به landing_page اضافه شدند، پس
// روی دیتابیس‌هایی که قبلاً seed شده بودند وجود ندارند. تا وقتی ادمین یک‌بار
// این بخش را ذخیره نکرده، همین‌جا برای پرشدن فرم با مقادیر فعلی سایت استفاده می‌شود
// (این مقادیر باید با appConfigService.js → DEFAULTS.landing_page هماهنگ بمانند).
const CONTACT_DEFAULTS = {
  fa: {
    contactTitle: 'ارتباط با ما',
    contactDesc: 'اگر سوال، پیشنهاد یا مشکلی دارید، فرم زیر را پر کنید تا مستقیماً به وزارت سحر و جادو و مدیران جامعه ارسال شود. همچنین می‌توانید از طریق دکمه‌های زیر به کانال تلگرام یا انجمن پاترهدها بپیوندید.',
    contactLinks: [
      { icon: '✈️', image: '', imageWidth: '', imageHeight: '', label: 'کانال تلگرام ما', url: 'https://t.me/WwWPotterHeadIR' },
      { icon: '👥', image: '', imageWidth: '', imageHeight: '', label: 'انجمن گفتگو', url: '/forum' }
    ]
  },
  en: {
    contactTitle: 'Contact Us',
    contactDesc: 'If you have any questions, feedback, or issues, please fill out the form below to send a message straight to our Hogwarts governors. You can also join our global Telegram community or discussion forum below.',
    contactLinks: [
      { icon: '✈️', image: '', imageWidth: '', imageHeight: '', label: 'Telegram Channel', url: 'https://t.me/potterheads_org' },
      { icon: '👥', image: '', imageWidth: '', imageHeight: '', label: 'Discussion Forum', url: '/forum' }
    ]
  }
};

// ── Small reusable image uploader: uploads immediately, shows preview ───────
// در صورت پاس‌دادن width/height و کال‌بک‌های مربوطه، فیلدهای «عرض/ارتفاع (px)»
// هم نمایش داده می‌شود تا سایز تصویر روی صفحه اصلی قابل تنظیم باشد. به‌همین
// ترتیب widthMobile/heightMobile یک سایز جداگانه و اختیاری برای حالت موبایل
// اضافه می‌کند — اگر پر نشود، سایز دسکتاپ (یا رفتار ریسپانسیو پیش‌فرض سایت)
// روی موبایل هم استفاده می‌شود.
function ImageField({
  label, value, onChange, width, height, onWidthChange, onHeightChange,
  widthMobile, heightMobile, onWidthMobileChange, onHeightMobileChange,
  sizable = true, shared = false, onUploadChange,
}) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const res = await adminAPI.uploadImage(file);
      if (res.data.success) {
        await Promise.resolve((onUploadChange || onChange)(res.data.path));
        message.success('تصویر آپلود شد');
      } else {
        message.error(res.data.message || 'خطا در آپلود تصویر');
      }
    } catch (err) {
      message.error(err.response?.data?.message || err.message || 'خطا در آپلود تصویر');
    } finally {
      setUploading(false);
    }
    return false; // جلوگیری از آپلود خودکار antd
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {label}
        {shared && (
          <span style={{ marginRight: 8, fontSize: 11, color: '#1677ff', background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 4, padding: '1px 6px' }}>
            🔗 مشترک بین همه زبان‌ها
          </span>
        )}
      </div>
      <Space align="start">
        {value && (
          <img
            src={value}
            alt=""
            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #d9d9d9' }}
            onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
          />
        )}
        <Space direction="vertical">
          <Upload beforeUpload={handleUpload} showUploadList={false} accept="image/*">
            <Button icon={<UploadOutlined />} loading={uploading}>
              {value ? 'تغییر تصویر' : 'آپلود تصویر'}
            </Button>
          </Upload>
          <Input
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="/uploads/..."
            style={{ width: 260 }}
          />
          {sizable && onWidthChange && onHeightChange && (
            <>
              <Space size={4}>
                <InputNumber
                  size="small"
                  min={0}
                  value={width || undefined}
                  onChange={(v) => onWidthChange(v || '')}
                  placeholder="عرض (px)"
                  style={{ width: 110 }}
                />
                <InputNumber
                  size="small"
                  min={0}
                  value={height || undefined}
                  onChange={(v) => onHeightChange(v || '')}
                  placeholder="ارتفاع (px)"
                  style={{ width: 110 }}
                />
                <span style={{ fontSize: 12, color: '#999' }}>دسکتاپ</span>
              </Space>
              {onWidthMobileChange && onHeightMobileChange && (
                <Space size={4}>
                  <InputNumber
                    size="small"
                    min={0}
                    value={widthMobile || undefined}
                    onChange={(v) => onWidthMobileChange(v || '')}
                    placeholder="عرض (px)"
                    style={{ width: 110 }}
                  />
                  <InputNumber
                    size="small"
                    min={0}
                    value={heightMobile || undefined}
                    onChange={(v) => onHeightMobileChange(v || '')}
                    placeholder="ارتفاع (px)"
                    style={{ width: 110 }}
                  />
                  <span style={{ fontSize: 12, color: '#999' }}>موبایل (اختیاری)</span>
                </Space>
              )}
            </>
          )}
        </Space>
      </Space>
    </div>
  );
}

// ── Generic editor for arrays of { icon, image, imageWidth, imageHeight, title, desc } ──
// هر مورد یا با اموجی (icon) یا با یک تصویر آپلودی (image) نمایش داده می‌شود؛
// اگر تصویر آپلود شود، به‌جای اموجی روی صفحه اصلی نشان داده می‌شود و سایزش هم قابل تنظیم است.
// imageOnly: فقط آپلود تصویر — برای بخش «تصاویر مشترک»
// textOnly: فقط متن (عنوان/توضیح) — برای تب‌های زبانی
function IconCardListEditor({ items, onChange, onUploadChange, withIcon = true, onImageSync, imageOnly = false, textOnly = false }) {
  const update = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const updateUpload = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    (onUploadChange || onChange)(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { ...EMPTY_ITEM }]);

  return (
    <div>
      {items.map((item, i) => (
        <Card
          size="small"
          key={i}
          style={{ marginBottom: 12 }}
          title={item.title || `مورد ${i + 1}`}
          extra={!textOnly && <Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} />}
        >
          {withIcon && !textOnly && (
            <>
              <ImageField
                label="تصویر (اختیاری — جایگزین اموجی می‌شود)"
                value={item.image}
                onChange={(v) => update(i, { image: v })}
                onUploadChange={(v) => updateUpload(i, { image: v })}
                width={item.imageWidth}
                height={item.imageHeight}
                onWidthChange={(v) => update(i, { imageWidth: v })}
                onHeightChange={(v) => update(i, { imageHeight: v })}
                widthMobile={item.imageWidthMobile}
                heightMobile={item.imageHeightMobile}
                onWidthMobileChange={(v) => update(i, { imageWidthMobile: v })}
                onHeightMobileChange={(v) => update(i, { imageHeightMobile: v })}
                shared
              />
              <Input
                style={{ marginBottom: 8, maxWidth: 140 }}
                value={item.icon}
                onChange={(e) => update(i, { icon: e.target.value })}
                placeholder="اموجی (اگر تصویری نباشد)"
              />
            </>
          )}
          {!imageOnly && (
            <>
              <Input
                style={{ marginBottom: 8 }}
                value={item.title}
                onChange={(e) => update(i, { title: e.target.value })}
                placeholder="عنوان"
              />
              <TextArea
                rows={2}
                value={item.desc}
                onChange={(e) => update(i, { desc: e.target.value })}
                placeholder="توضیح"
              />
            </>
          )}
        </Card>
      ))}
      {!textOnly && <Button size="small" icon={<PlusOutlined />} onClick={add}>افزودن مورد</Button>}
    </div>
  );
}

// ── Contact links editor (array of { icon, image, label, url }) ────────────
// هر دکمه‌ی «تماس با ما» یا اموجی دارد، یا تصویر آپلودی (جایگزین اموجی می‌شود)،
// یا هیچ‌کدام (فقط متن ساده) — و یک لینک که هم می‌تواند آدرس خارجی باشد
// (تلگرام، اینستاگرام، ایمیل mailto:...) و هم مسیر داخلی سایت (/forum).
function ContactLinksEditor({ items, onChange, onUploadChange, onImageSync, imageOnly = false, textOnly = false }) {
  const update = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const updateUpload = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    (onUploadChange || onChange)(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { ...CONTACT_EMPTY_ITEM }]);

  return (
    <div>
      {items.map((item, i) => (
        <Card
          size="small"
          key={i}
          style={{ marginBottom: 12 }}
          title={item.label || `دکمه ${i + 1}`}
          extra={!textOnly && <Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} />}
        >
          {!textOnly && (
            <>
              <ImageField
                label="تصویر (اختیاری — جایگزین اموجی)"
                value={item.image}
                onChange={(v) => update(i, { image: v })}
                onUploadChange={(v) => updateUpload(i, { image: v })}
                width={item.imageWidth}
                height={item.imageHeight}
                onWidthChange={(v) => update(i, { imageWidth: v })}
                onHeightChange={(v) => update(i, { imageHeight: v })}
                widthMobile={item.imageWidthMobile}
                heightMobile={item.imageHeightMobile}
                onWidthMobileChange={(v) => update(i, { imageWidthMobile: v })}
                onHeightMobileChange={(v) => update(i, { imageHeightMobile: v })}
                shared
              />
              <Input
                style={{ marginBottom: 8, maxWidth: 140 }}
                value={item.icon}
                onChange={(e) => update(i, { icon: e.target.value })}
                placeholder="اموجی (اگر تصویری نباشد)"
              />
            </>
          )}
          {!imageOnly && (
            <>
              <Input
                style={{ marginBottom: 8 }}
                value={item.label}
                onChange={(e) => update(i, { label: e.target.value })}
                placeholder="متن دکمه"
              />
              <Input
                value={item.url}
                onChange={(e) => update(i, { url: e.target.value })}
                placeholder="لینک — مثلاً https://t.me/... یا /forum یا mailto:..."
              />
            </>
          )}
        </Card>
      ))}
      {!textOnly && <Button size="small" icon={<PlusOutlined />} onClick={add}>افزودن دکمه</Button>}
    </div>
  );
}

// ── Bullet list editor (array of plain strings) ─────────────────────────────
function BulletListEditor({ items, onChange }) {
  const update = (i, v) => {
    const next = items.slice();
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, '']);

  return (
    <div>
      {items.map((item, i) => (
        <Row gutter={8} key={i} style={{ marginBottom: 8 }}>
          <Col flex="auto">
            <Input value={item} onChange={(e) => update(i, e.target.value)} />
          </Col>
          <Col flex="32px">
            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} />
          </Col>
        </Row>
      ))}
      <Button size="small" icon={<PlusOutlined />} onClick={add}>افزودن</Button>
    </div>
  );
}

// ── Journey steps editor (fixed order, each has image + title + desc) ──────
function JourneyStepsEditor({ steps, onChange, onUploadChange, onImageSync, imageOnly = false, textOnly = false }) {
  const update = (i, patch) => {
    const next = steps.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const updateUpload = (i, patch) => {
    const next = steps.slice();
    next[i] = { ...next[i], ...patch };
    (onUploadChange || onChange)(next);
  };
  const remove = (i) => onChange(steps.filter((_, idx) => idx !== i));
  const add = () => onChange([...steps, { step: String(steps.length + 1), image: '', title: '', desc: '' }]);

  return (
    <div>
      {steps.map((s, i) => (
        <Card size="small" key={i} style={{ marginBottom: 12 }} title={`مرحله ${s.step || i + 1}`}
          extra={!textOnly && <Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} />}>
          {!textOnly && (
            <ImageField
              label="تصویر"
              value={s.image}
              onChange={(v) => update(i, { image: v })}
              onUploadChange={(v) => updateUpload(i, { image: v })}
              width={s.imageWidth}
              height={s.imageHeight}
              onWidthChange={(v) => update(i, { imageWidth: v })}
              onHeightChange={(v) => update(i, { imageHeight: v })}
              widthMobile={s.imageWidthMobile}
              heightMobile={s.imageHeightMobile}
              onWidthMobileChange={(v) => update(i, { imageWidthMobile: v })}
              onHeightMobileChange={(v) => update(i, { imageHeightMobile: v })}
              shared
            />
          )}
          {!imageOnly && (
            <>
              <Input
                style={{ marginBottom: 8 }}
                value={s.title}
                onChange={(e) => update(i, { title: e.target.value })}
                placeholder="عنوان مرحله"
              />
              <TextArea
                rows={2}
                value={s.desc}
                onChange={(e) => update(i, { desc: e.target.value })}
                placeholder="توضیح مرحله"
              />
            </>
          )}
        </Card>
      ))}
      {!textOnly && <Button size="small" icon={<PlusOutlined />} onClick={add}>افزودن مرحله</Button>}
    </div>
  );
}

export default function LandingContentManager() {
  const [content, setContent] = useState(Object.fromEntries(SUPPORTED_LANGS.map((l) => [l, {}])));
  const contentRef = useRef(content);
  const [activeLang, setActiveLang] = useState('fa');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { contentRef.current = content; }, [content]);
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getLandingContent();
      if (res.data.success) {
        const value = res.data.config?.value || {};
        // همه زبان‌های پشتیبانی‌شده را init کن؛ زبان‌های تازه از en به عنوان fallback می‌گیرند
        SUPPORTED_LANGS.forEach((lng) => {
          value[lng] = value[lng] || {};
          const defaults = CONTACT_DEFAULTS[lng] || CONTACT_DEFAULTS['en'];
          if (value[lng].contactTitle === undefined) value[lng].contactTitle = defaults.contactTitle;
          if (value[lng].contactDesc === undefined) value[lng].contactDesc = defaults.contactDesc;
          if (value[lng].contactLinks === undefined) value[lng].contactLinks = defaults.contactLinks;
          if (value[lng].waitlistIcon === undefined) value[lng].waitlistIcon = '📬';
        });
        // اندازه‌ی متن‌ها (textSizes) کلید مشترک بین دو زبان است، نه زیرمجموعه‌ی fa/en
        if (value.textSizes === undefined) value.textSizes = {};
        contentRef.current = value;
        setContent(value);
      }
    } catch (err) {
      message.error('خطا در بارگذاری محتوای صفحه اصلی');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.upsertLandingContent(content);
      if (res.data.success) {
        message.success('محتوای صفحه اصلی ذخیره شد');
      }
    } catch (err) {
      message.error('خطا در ذخیره محتوا');
    } finally {
      setSaving(false);
    }
  };

  const patchLang = (patch) => {
    setContent((prev) => ({ ...prev, [activeLang]: { ...prev[activeLang], ...patch } }));
  };

  // به‌روزرسانی همزمان همه زبان‌ها — برای تصاویر که باید مشترک باشند
  const patchAll = (patch) => {
    setContent((prev) => {
      const next = { ...prev };
      SUPPORTED_LANGS.forEach((lng) => { next[lng] = { ...prev[lng], ...patch }; });
      contentRef.current = next;
      return next;
    });
  };

  const patchAllAndSave = async (patch) => {
    const next = { ...contentRef.current };
    SUPPORTED_LANGS.forEach((lng) => {
      next[lng] = { ...(next[lng] || {}), ...patch };
    });
    contentRef.current = next;
    setContent(next);
    const res = await adminAPI.upsertLandingContent(next);
    if (!res.data.success) {
      throw new Error(res.data.message || 'خطا در ذخیره تصویر');
    }
  };

  const patchAllArrayImagesAndSave = (arrayKey, defaultItem) => async (newFaArr) => {
    const prev = contentRef.current;
    const next = { ...prev, fa: { ...(prev.fa || {}), [arrayKey]: newFaArr } };
    SUPPORTED_LANGS.filter((l) => l !== 'fa').forEach((lng) => {
      const lngArr = (prev[lng]?.[arrayKey] || []).slice();
      newFaArr.forEach((item, i) => {
        while (lngArr.length <= i) lngArr.push({ ...defaultItem });
        const imgPatch = {};
        IMAGE_KEYS.forEach((k) => { imgPatch[k] = item[k] ?? ''; });
        lngArr[i] = { ...lngArr[i], ...imgPatch };
      });
      if (newFaArr.length < lngArr.length) lngArr.splice(newFaArr.length);
      next[lng] = { ...(prev[lng] || {}), [arrayKey]: lngArr };
    });
    contentRef.current = next;
    setContent(next);
    const res = await adminAPI.upsertLandingContent(next);
    if (!res.data.success) {
      throw new Error(res.data.message || 'خطا در ذخیره تصویر');
    }
  };

  const patchAllArrayImages = (arrayKey, defaultItem) => (newFaArr) => {
    setContent((prev) => {
      const next = { ...prev, fa: { ...(prev.fa || {}), [arrayKey]: newFaArr } };
      SUPPORTED_LANGS.filter((l) => l !== 'fa').forEach((lng) => {
        const lngArr = (prev[lng]?.[arrayKey] || []).slice();
        newFaArr.forEach((item, i) => {
          while (lngArr.length <= i) lngArr.push({ ...defaultItem });
          const imgPatch = {};
          IMAGE_KEYS.forEach((k) => { imgPatch[k] = item[k] ?? ''; });
          lngArr[i] = { ...lngArr[i], ...imgPatch };
        });
        if (newFaArr.length < lngArr.length) lngArr.splice(newFaArr.length);
        next[lng] = { ...(prev[lng] || {}), [arrayKey]: lngArr };
      });
      contentRef.current = next;
      return next;
    });
  };

  // وقتی در بخش «تصاویر مشترک» کل آرایه (journeySteps/features/...) تغییر می‌کند:
  // — آرایه‌ی fa با کل newArr جایگزین می‌شود
  // — در همه زبان‌های دیگر فقط فیلدهای تصویر merge می‌شود؛ متن‌های اختصاصی دست‌نخورده می‌مانند
  const IMAGE_KEYS = ['image', 'imageWidth', 'imageHeight', 'imageWidthMobile', 'imageHeightMobile'];
  if (loading) return <Spin style={{ display: 'block', margin: '48px auto' }} />;

  // محتوای زبان فعال — برای فیلدهای متنی
  const c = content[activeLang] || {};
  // محتوای فارسی — مرجع تصاویر مشترک (چون هر دو باید یکسان باشند)
  const sharedImages = content.fa || {};

  return (
    <div style={{ maxWidth: 900 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="متن‌ها و تصاویر صفحه اصلی سایت"
        description="تصاویر مشترک بین همه زبان‌ها هستند و فقط یک‌بار تنظیم می‌شوند (بخش «تصاویر مشترک» در پایین). متن‌ها جداگانه برای هر زبان در تب مربوطه ویرایش می‌شوند."
      />

      {/* ══ تصاویر مشترک — خارج از تب‌های زبانی ══════════════════════════════ */}
      <Card
        title="🖼️ تصاویر مشترک بین همه زبان‌ها"
        style={{ marginBottom: 16, borderColor: '#1677ff' }}
        headStyle={{ background: '#e6f4ff' }}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="تصاویر این بخش برای همه زبان‌ها (فارسی و انگلیسی) یکسان است. فقط یک‌بار آپلود کنید."
        />

        <Divider orientation="left" plain>تصویر هیرو (قلعه)</Divider>
        <ImageField
          label="تصویر قلعه (هیرو)"
          value={sharedImages.heroImage}
          onChange={(v) => patchAll({ heroImage: v })}
          onUploadChange={(v) => patchAllAndSave({ heroImage: v })}
          width={sharedImages.heroImageWidth}
          height={sharedImages.heroImageHeight}
          onWidthChange={(v) => patchAll({ heroImageWidth: v })}
          onHeightChange={(v) => patchAll({ heroImageHeight: v })}
          widthMobile={sharedImages.heroImageWidthMobile}
          heightMobile={sharedImages.heroImageHeightMobile}
          onWidthMobileChange={(v) => patchAll({ heroImageWidthMobile: v })}
          onHeightMobileChange={(v) => patchAll({ heroImageHeightMobile: v })}
          shared
        />

        <Divider orientation="left" plain>آیکون لیست انتظار</Divider>
        <ImageField
          label="تصویر آیکون (اختیاری — جایگزین اموجی می‌شود)"
          value={sharedImages.waitlistIconImage}
          onChange={(v) => patchAll({ waitlistIconImage: v })}
          onUploadChange={(v) => patchAllAndSave({ waitlistIconImage: v })}
          width={sharedImages.waitlistIconImageWidth}
          height={sharedImages.waitlistIconImageHeight}
          onWidthChange={(v) => patchAll({ waitlistIconImageWidth: v })}
          onHeightChange={(v) => patchAll({ waitlistIconImageHeight: v })}
          shared
        />
        <Input
          style={{ maxWidth: 140, marginTop: 8 }}
          value={sharedImages.waitlistIcon}
          onChange={(e) => patchAll({ waitlistIcon: e.target.value })}
          placeholder="اموجی (اگر تصویری نباشد)"
        />

        <Divider orientation="left" plain>تصاویر مراحل ورود (Journey Steps)</Divider>
        <JourneyStepsEditor
          steps={sharedImages.journeySteps || []}
          onChange={patchAllArrayImages('journeySteps', { step: '', image: '', title: '', desc: '' })}
          onUploadChange={patchAllArrayImagesAndSave('journeySteps', { step: '', image: '', title: '', desc: '' })}
          imageOnly
        />

        <Divider orientation="left" plain>تصاویر ویژگی‌ها (Features)</Divider>
        <IconCardListEditor
          items={sharedImages.features || []}
          onChange={patchAllArrayImages('features', EMPTY_ITEM)}
          onUploadChange={patchAllArrayImagesAndSave('features', EMPTY_ITEM)}
          imageOnly
        />

        <Divider orientation="left" plain>تصاویر ویترین جادویی (Magic Showcase)</Divider>
        <IconCardListEditor
          items={sharedImages.magicShowcase || []}
          onChange={patchAllArrayImages('magicShowcase', EMPTY_ITEM)}
          onUploadChange={patchAllArrayImagesAndSave('magicShowcase', EMPTY_ITEM)}
          imageOnly
        />

        <Divider orientation="left" plain>تصاویر ویژگی‌های بیشتر (More Features)</Divider>
        <IconCardListEditor
          items={sharedImages.moreFeatures || []}
          onChange={patchAllArrayImages('moreFeatures', EMPTY_ITEM)}
          onUploadChange={patchAllArrayImagesAndSave('moreFeatures', EMPTY_ITEM)}
          imageOnly
        />

        <Divider orientation="left" plain>تصاویر دکمه‌های تماس (Contact Links)</Divider>
        <ContactLinksEditor
          items={sharedImages.contactLinks || []}
          onChange={patchAllArrayImages('contactLinks', CONTACT_EMPTY_ITEM)}
          onUploadChange={patchAllArrayImagesAndSave('contactLinks', CONTACT_EMPTY_ITEM)}
          imageOnly
        />
      </Card>

      {/* ══ متن‌های زبان‌خاص ══════════════════════════════════════════════════ */}
      <Alert
        type="success"
        showIcon
        style={{ marginBottom: 12 }}
        message="متن‌های زبان‌خاص — تب زبان مورد نظر را انتخاب کنید"
      />

      <Tabs
        activeKey={activeLang}
        onChange={setActiveLang}
        items={SUPPORTED_LANGS.map((lng) => ({ key: lng, label: LANG_LABELS[lng] }))}
      />

      <Card title="هیرو (بخش اول صفحه)" style={{ marginBottom: 16 }}>
        <Input addonBefore="شعار کوتاه (Tagline)" value={c.tagline} onChange={(e) => patchLang({ tagline: e.target.value })} style={{ marginBottom: 8 }} />
        <Input addonBefore="عنوان اصلی" value={c.heroTitle} onChange={(e) => patchLang({ heroTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="توضیح زیر عنوان" value={c.heroSubtitle} onChange={(e) => patchLang({ heroSubtitle: e.target.value })} style={{ marginBottom: 8 }} />
        <Input addonBefore="متن دکمه CTA" value={c.ctaText} onChange={(e) => patchLang({ ctaText: e.target.value })} style={{ marginBottom: 8 }} />
        <Divider orientation="left" plain>نکات کوتاه (Bullets)</Divider>
        <BulletListEditor items={c.heroBullets || []} onChange={(v) => patchLang({ heroBullets: v })} />
      </Card>

      <Card title="مسیر ورود (Journey / Story)" style={{ marginBottom: 16 }}>
        <Input addonBefore="برچسب بالای عنوان" value={c.storyLabel} onChange={(e) => patchLang({ storyLabel: e.target.value })} style={{ marginBottom: 8 }} />
        <Input addonBefore="عنوان" value={c.storyTitle} onChange={(e) => patchLang({ storyTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="توضیح" value={c.storyDesc} onChange={(e) => patchLang({ storyDesc: e.target.value })} style={{ marginBottom: 12 }} />
        <Divider orientation="left" plain>متن مراحل (تصاویر را در بخش «تصاویر مشترک» بالا ویرایش کنید)</Divider>
        <JourneyStepsEditor
          steps={c.journeySteps || []}
          onChange={(v) => patchLang({ journeySteps: v })}
          textOnly
        />
      </Card>

      <Card title="ویژگی‌ها (Features)" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان بخش" value={c.featuresTitle} onChange={(e) => patchLang({ featuresTitle: e.target.value })} style={{ marginBottom: 12 }} />
        <IconCardListEditor
          items={c.features || []}
          onChange={(v) => patchLang({ features: v })}
          textOnly
        />
      </Card>

      <Card title="ویترین جادویی (Magic Showcase)" style={{ marginBottom: 16 }}>
        <Input addonBefore="برچسب" value={c.magicShowcaseLabel} onChange={(e) => patchLang({ magicShowcaseLabel: e.target.value })} style={{ marginBottom: 8 }} />
        <Input addonBefore="عنوان" value={c.magicShowcaseTitle} onChange={(e) => patchLang({ magicShowcaseTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="توضیح" value={c.magicShowcaseDesc} onChange={(e) => patchLang({ magicShowcaseDesc: e.target.value })} style={{ marginBottom: 12 }} />
        <IconCardListEditor
          items={c.magicShowcase || []}
          onChange={(v) => patchLang({ magicShowcase: v })}
          textOnly
        />
      </Card>

      <Card title="ویژگی‌های بیشتر (More Features)" style={{ marginBottom: 16 }}>
        <IconCardListEditor
          items={c.moreFeatures || []}
          onChange={(v) => patchLang({ moreFeatures: v })}
          textOnly
        />
      </Card>

      <Card title="تماس با ما (Contact Us)" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان" value={c.contactTitle} onChange={(e) => patchLang({ contactTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="توضیح" value={c.contactDesc} onChange={(e) => patchLang({ contactDesc: e.target.value })} style={{ marginBottom: 12 }} />
        <Divider orientation="left" plain>متن دکمه‌های تماس (تصاویر در «تصاویر مشترک» بالا)</Divider>
        <ContactLinksEditor
          items={c.contactLinks || []}
          onChange={(v) => patchLang({ contactLinks: v })}
          textOnly
        />
      </Card>

      <Card title="حمایت مالی (Support)" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان" value={c.supportTitle} onChange={(e) => patchLang({ supportTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="توضیح" value={c.supportDesc} onChange={(e) => patchLang({ supportDesc: e.target.value })} style={{ marginBottom: 8 }} />
        <Input addonBefore="متن دکمه" value={c.supportButton} onChange={(e) => patchLang({ supportButton: e.target.value })} />
      </Card>

      <Card title="لیست انتظار (Waitlist)" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان" value={c.waitlistTitle} onChange={(e) => patchLang({ waitlistTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="توضیح" value={c.waitlistDesc} onChange={(e) => patchLang({ waitlistDesc: e.target.value })} style={{ marginBottom: 12 }} />
      </Card>

      <Card title="اندازه متن‌ها (دسکتاپ / موبایل)" style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="این تنظیم بین فارسی و انگلیسی مشترک است و مستقل از تب زبان بالا عمل می‌کند."
          description="هر فیلد را خالی بگذارید تا همان اندازه‌ی پیش‌فرض و ریسپانسیو فعلی سایت استفاده شود. مقدار موبایل اختیاری است؛ اگر خالی باشد از مقدار دسکتاپ (یا پیش‌فرض) استفاده می‌شود."
        />
        <TextSizeEditor
          sizes={content.textSizes || {}}
          onChange={(v) => setContent((prev) => ({ ...prev, textSizes: v }))}
        />
      </Card>

      <Space style={{ marginBottom: 40 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
          ذخیره همه تغییرات (همه زبان‌ها)
        </Button>
        <Button icon={<ReloadOutlined />} onClick={load}>بازخوانی از سرور</Button>
      </Space>
    </div>
  );
}
